// @flow strict

/**
 * Kuupäeva ja kellaaja andmetüüp.
 * Väärtused peavad olema stringid kujul: `YYYY-MM-DDThh:mm:ss`
 */
export type IsoDateTimeStringType = string;

/**
 * Visuaalselt loetav kuupäeva ja kellaaja andmetüüp.
 * Väärtused peavad olema stringid kujul: `YYYY-MM-DD hh:mm:ss`
 */
export type DateTimeStringType = string;

/**
 * Kuupäeva andmetüüp
 * Väärtused peavad olema stringid kujul: `YYYY-MM-DD`
 */
//type IsoDateStringType = string;

/**
 * Täisarvu andmetüüp
 */
export type IntegerType = number;

/**
 * Manuaalse mõõtmise info
 */
export type MeasuringModeType = {
    "mode": number,
    "tooteKood": string,
    "tooteVersioon": number,
    "measuringRequested": number,
    "measuringInProgress": number,
    "hornReset": number
};

/**
 * Teenuse /manualMeasuring poolt tagastatav objekt
 */
export type ManualMeasuringResultType = {
    "manualMeasuringResult": MeasuringModeType
};

/**
 * Toote otspunkti kirjeldus
 */
export type EndpointType = {
    /**
     * Otspunkti X koordinaat
     */
    x: IntegerType,
    /**
     * Otspunkti Y koordinaat
     */
    y: IntegerType,
    /**
     * Otspunkti Z koordinaat
     */
    z: IntegerType,
    /**
     * Otspunkti mõõtmise kalle x teljel
     */
    slopeX: IntegerType,
    /**
     * Otspunkti mõõtmise kalle y teljel
     */
    slopeY: IntegerType,
    /**
     * Prindi kaugus klaasi servast
     */
    printOffset: IntegerType
};

export type ToleranceType = {
    /**
     * Nominaali nimi. Kohustuslik. Peab olema üks järgnevatest:
     * A - joone alguses olev nominaalpunkt
     * B - joone keskel olev nominaalpunkt
     * C - jooone lõpus olev nominaalpunkt
     * P[j] - keskmise joone peal asuv peeglialuse ala nominaalpunkt, siin j on 50, 100, 150, 200 või 250
     */
    name: string,
    /**
     * Nominaali ettenähtud mõõteväärtus
     */
    value: IntegerType,
    /**
     * ülemine 1. tolerants
     */
    upperTolerance1: IntegerType,
    /**
     * alumine 1. tolerants
     */
    lowerTolerance1: IntegerType,
    /**
     * ülemine 2. tolerants
     */
    upperTolerance2: IntegerType,
    /**
     * alumine 2. tolerants
     */
    lowerTolerance2: IntegerType
}

/**
 * Toote nominaalpunkti kirjeldus
 */
export type NominalPointType = ToleranceType & {
    /**
     * Kaugus klaasi servast
     */
    distance: IntegerType,
    /**
     * Korrektsioon
     */
    correction: IntegerType,
    /**
     * Kas on "ADAS akna punkt"
     */
    isAdas: boolean,
    /**
     * Kas punkt kuvatkase mõõtmise ekraanil
     */
    show: boolean
};

/**
 * Toote nominaalnurga kirjeldus
 */
export type NominalAngleType = ToleranceType & {
    /**
     * 1. punkti nimi
     */
    point1: string,
    /**
     * 2. punkti nimi
     */
    point2: string
};

/**
 * Mõõtejoone info struktuur
 */
export type MeasurementLineType = {
    /**
     * Mõõtejoone pikkus
     */
    lineLength: IntegerType,
    /**
     * Otspunktide andmed - esimene on alguspunkt ja teine lõpp-punkt.
     */
    endpoints: [EndpointType, EndpointType],
    /**
     * Mõõtepunktid
     */
    nominals: Array<NominalPointType>,
    /**
     * Mõõtenurgad
     */
    angles?: Array<NominalAngleType>
};

/**
 * Tooteinfo struktuur
 */
export type ProductType = {
    /**
     * Tooteinfo versioon, peab olema väärtus `3`
     */
    version: IntegerType,
    /**
     * Tooteinfos esitatud mõõtejoonte arv
     */
    lineCount: IntegerType,
    /**
     * Toote kood ja variant. Esitatakse kujul "{code}_{variant}". Ei edastata PLCsse
     */
    codeVariant: string,
    /**
     * Toote SCAN kood. Ei edastata PLCsse
     */
    scanCode: string,
    /**
     * Toote nimetus (description). Ei edastata PLCsse
     */
    description: string,
    /**
     * Iga element vastab ühele mõõtejoonele, kuni viis mõõtejoont
     */
    measurementLines: Array<MeasurementLineType>,
    /**
     * Klaasi paksus
     */
    glassThickness: IntegerType,
    /**
     * Lasersersensori mõõteprofiil klaasi jaoks
     */
    measuringProfileGlass: IntegerType,
    /**
     * Lasersersensori mõõteprofiil prindi jaoks
     */
    measuringProfilePrint: IntegerType,
    /**
     * Klaasi joone max ja min x koordinaatide vahe. Ei edastata PLCsse
     */
    diffX: IntegerType,
    /**
     * Klaasi joone max ja min y koordinaatide vahe. Ei edastata PLCsse
     */
    diffY: IntegerType,
    /**
     * Klaasijoone nihutus x-teljel. Nihkeväärtus liidetakse juurde kõikide otspunktide x-väärtustele
     */
    offsetX: IntegerType,
    /**
     * Klaasijoone nihutus y-teljel. Nihkeväärtus liidetakse juurde kõikide otspunktide y-väärtustele
     */
    offsetY: IntegerType,
    /**
     * GR2 faili enihe parameeter. Ignoreeritakse, ei edastata PLCsse
     */
    offsetE: IntegerType,
    /**
     * Mõõtejoonte ülemiste punktide X-koordinaadid. Ei edastata PLCsse
     */
    lineUpper: Array<IntegerType>,
    /**
     * Mõõtejoonte alumiste punktide X-koordinaadid. Ei edastata PLCsse
     */
    lineLower: Array<IntegerType>,
    /**
     * Mõõtepunktide x-koordinaadid. Ei edastata PLCsse
     */
    pX: Array<IntegerType>,
    /**
     * Mõõtepunktide y-koordinaadid. Ei edastata PLCsse
     */
    pY: Array<IntegerType>,
    /**
     * Kas mõõtejoonte failis on mõõtejoon keeratud
     */
    rotatedLine: boolean,
    /**
     * Keskmiste mõõtepunktide 1. tolerants
     */
    toleranceMiddle1: IntegerType,
    /**
     * Keskmiste mõõtepunktide 2. tolerants
     */
    toleranceMiddle2: IntegerType,
    /**
     * Serva mõõtepunktide 1. tolerants
     */
    toleranceEdge1: IntegerType,
    /**
     * Serva mõõtepunktide 2. tolerants
     */
    toleranceEdge2: IntegerType,
    /**
     * Kas mõõtejoon tuleb esitada peegeldatuna
     */
    mirrored: boolean,
    /**
     * Edastatakse PLCsse, kui konfiguratsioonis on "rotated_measurements_allowed" tõene
     */
    rotatedFirst: boolean,
    /**
     * Edastatakse PLCsse, kui konfiguratsioonis on "rotated_measurements_allowed" tõene
     */
    rotatedLast: boolean,
    /**
     * Kas on keeratud 90 kraadi
     */
    rotated: boolean
};

/**
 * Mõõteinfo struktuur
 */
export type MeasurementType =  {
    /**
     * Vaguni number, millel mõõtmine teostati
     */
    "vaguni_number": IntegerType,
    /**
     * Kas antud vagunit on lubatud mõõta
     */
    "mootmine_luba": boolean,
    /**
     * Kas antud vagunil on kordusmõõtmine lubatud
     */
    "kordusmootmine_luba": boolean,
    /**
     * Vagunil oleva toote kood koos variandiga
     */
    "toote_variant": string,
    /**
     * Toote kirjeldus 1
     */
    "kirjeldus1": string,
    /**
     * Toote kirjeldus 2
     */
    "kirjeldus2": string,
    /**
     * Ajahetk, mil PLC soovis tarkvaralt saada antud vagunil oleva toote infot
     */
    "algus_aeg": IsoDateTimeStringType,
    /**
     * Kui tõene, siis teostatakse ainult ääreotsing
     */
    "aareotsing" : boolean,
    /**
     * Filter 1 on kasutuses
     */
    "filter": boolean,
    /**
     * Filter 1 akna laius
     */
    "filter_kordus": IntegerType,
    /**
     * Filter 1 max viga
     */
    "filter_koefitsient": IntegerType,
    /**
     * Filter 1 ignoreeritud serv
     */
    "filter_viga_max": IntegerType,
    /**
     * Filter 2 on kasutuses
     */
    "filter2": boolean,
    /**
     * Filter 2 akna laius
     */
    "filter2_kordus": IntegerType,
    /**
     * Filter 2 max viga
     */
    "filter2_koefitsient": IntegerType,
    /**
     * Filter 2 ignoreeritud serv
     */
    "filter2_viga_max": IntegerType,
    /**
     * Kasutatav keel
     */
    "keel": IntegerType,
    /**
     * Mõõtejoonte arv
     */
    "joonte_arv": IntegerType,
    /**
     * Vaguni raami number
     */
    "vaguni_raam": IntegerType,
    /**
     * Ahju ringi number
     */
    "ahju_ring": IntegerType,
    /**
     * Klaasi ID
     */
    "klaasi_id": IntegerType,
    /**
     * Veakood
     */
    "veakood": IntegerType,
    /**
     * Ajahetk, mil PLC tagastas mõõtmistulemuse
     */
    "lopp_aeg": IsoDateTimeStringType,
    /**
     * Viimased 100 akumuleerunud veateadet
     */
    "viga": Array<string>,
    /**
     * Kolmemõtmeline massiiv, kus on esitatud mõõtejoonte mõõtmiste mõõteväärtused, mm, skaleeritud 10x.
     * Juhul, kui PLC teostas mingi mõõtejoone korral vähem kui kolm mõõtmist, on massiivis ka mõõtmiselemente
     * vastavalt vähem. Kui PLC ei teostanud ühtegi mõõtmist, siis on see mõõtejoone massiiv tühi (st mitte null,
     * vaid massiiv 0 elemendiga).
     * Massiivi tasemed:
     *  - mõõtejoon (1..5)
     *  - mõõtmine (1..3)
     *  - mõõtmise punktid (1..100)
     */
    "mootejoon": Array<Array<Array<IntegerType>>>,
    /**
     * Kahemõõtmeline massiiv, kus esitatud mõõtejoonte mõõtmise toorväärtused, mm, skaleeritud 10x. Kui mingi joone
     * korral teostati vähem kui 500 mõõtmist, siis on ka massiiv vastavalt väiksem.
     */
    "mootejoon_toor": Array<Array<IntegerType>>,
    /**
     * Mõõtejoonte tooteinfo, üks element iga mõõtejoone kohta
     */
    "tooteinfo": Array<MeasurementLineType>
};

export type MeasurementWithPasswordType = MeasurementType & {
    password: string
};

/**
 * KLPMS Web sisemiselt mõõtmise kohta hoitav info
 */
export type ConvertedMeasurementType = {
    /**
     * wagon Vaguni number: 0 -> manual, 1 ... n
     */
    "wagon": string,
    /**
     * Vaguni raam
     */
    "skeleton": string,
    /**
     * Toote kood ja variant
     */
    "codeVariant": string,
    /**
     * Ahju ring
     */
    "furnaceRing": number,
    /**
     * Klaasi ID
     */
    "glassId": number,
    /**
     * Kirjeldus 1
     */
    "description1": string,
    /**
     * Kirjeldus 2
     */
    "description2": string,
    /**
     *  Mõõtmise alustamise ajamoment kujul "YYYY-MM-DD hh:mm:ss" (kui puudub, siis "?")
     */
    "timestamp": string,
    /**
     * Lõpetamise ajamoment kujul "YYYY-MM-DD hh:mm:ss" (kui puudub, siis "?")
     */
    "end_timestamp": string,
    /**
     *  Joone pikkuste massiiv (üks pikkus iga joone kohta)
     */
    "lineends": Array<IntegerType>,
    /**
     * Nominaalide kaugused joone algusest (järjekorras P1, ..., P9/P15, P50, ..., P300, H1, ..., H5)
     */
    "linepoints": Array<IntegerType>,
    /**
     * Kõikide nominaalide info (järjekorras P1, ..., P9/P15, P50, ..., P300, H1, ..., H5).
     * Nominaali info on omakorda massiiv väärtustest:
     * Nominaali väärtuse ja tolerantside info:
     *  [v, t1p, t1m, t2p, t2m],
     *  kus:
     *      v - nominaalväärtus,
     *      t1p - ülemine 1. tolerants,
     *      t1m - alumine 1. tolerants,
     *      t2p - ülemine 2. tolerants,
     *      t2m - alumine 2. tolerants
     */
    "required": Array<Array<[IntegerType, IntegerType, IntegerType, IntegerType, IntegerType]>>,
    /**
     *  Kõikide nominaalnurkade info (järjekorras ANGLE)
     * Nominaali info on omakorda massiiv väärtustest:
     * Nominaali väärtuse ja tolerantside info:
     *  [v, t1p, t1m, t2p, t2m],
     *  kus:
     *      v - nominaalväärtus,
     *      t1p - ülemine 1. tolerants,
     *      t1m - alumine 1. tolerants,
     *      t2p - ülemine 2. tolerants,
     *      t2m - alumine 2. tolerants
     */
    "angles": Array<Array<[IntegerType, IntegerType, IntegerType, IntegerType, IntegerType]>>,
    /**
     *  Mõõtmiste info:
     *  - Saab olla 1, 2, või 3 mõõtmist
     *  - Iga mõõtmisinfo saab olla 3 või 5 mõõtejoont
     *  - Mõõtejoone mõõtepunktide väärtused saab olla kas 100 või 200 elementi
     */
    "series": Array<Array<Array<IntegerType>>>,
    /**
     * Toormõõteandmed viimase mõõtmise kohta:
     * - Saab olla 3 või 5 mõõtejoont
     * - Mõõtejoone mõõtepunktide toorväärtused saab olla kas 500 või 1000 elementi
     */
    "rawdata": Array<Array<IntegerType>>,
    /**
     * Veateadete massiiv, kõige uuem on esimene
     */
    "error": Array<string>,
    /**
     *  Korrigeeritud mõõteandmete info:
     *  - Saab olla 1, 2, või 3 mõõtmist
     *  - Iga mõõtmisinfo saab olla 3 või 5 mõõtejoont
     *  - Mõõtejoone mõõtepunktide väärtused saab olla kas 100 või 200 elementi
     */
    "corr_series": Array<Array<Array<IntegerType>>>,
    /**
     * Korrigeeritud nominaalide info (järjekorras A1, ..., C3/E3, [P50, ..., P300]).
     * Nominaali info on omakorda massiiv väärtustest:
     * Nominaali väärtuse ja tolerantside info:
     *  [v, t1p, t1m, t2p, t2m],
     *  kus:
     *      v - nominaalväärtus,
     *      t1p - ülemine 1. tolerants,
     *      t1m - alumine 1. tolerants,
     *      t2p - ülemine 2. tolerants,
     *      t2m - alumine 2. tolerants
     */
    "corr_required": Array<Array<[IntegerType, IntegerType, IntegerType, IntegerType, IntegerType]>>,
    /**
     *  Korrigeeritud mõõteandmete erinevused algsetest info:
     *  - Saab olla 1, 2, või 3 mõõtmist
     *  - Iga mõõtmisinfo saab olla 3 või 5 mõõtejoont
     *  - Mõõtejoone mõõtepunktide väärtused saab olla kas 100 või 200 elementi
     */
    "corr_shifts": Array<Array<Array<IntegerType>>>,
    /**
     *  Analüüsitud nominaalide info:
     *  - Kõikide nominaalide analüüsiinfo (järjekorras A1, ..., C3/E3, [P50, ..., P300])
     *  - Nominaali analüüsimise tulemus: massiiv kujul
     *    [diff, status, y, x], kus
     *      diff - punkti erinevus nominaalväärtusest,
     *      status - 1: ok (diff < alumine viga), 2: warning (diff < ülemine viga), 3: error (diff > ülemine viga),
     *      y - punkti tegelik väärtus,
     *      x - nominaali x koordinaat
     */
    "analyzed": ?Array<[IntegerType, IntegerType, number, IntegerType]>,
    /**
     *  Analüüsitud nurkade info:
     *  - Kõikide nominaalide analüüsiinfo (järjekorras A1, ..., C3/E3, [P50, ..., P300])
     *  - Nominaali analüüsimise tulemus: massiiv kujul
     *    [diff, status, y, x], kus
     *      diff - punkti erinevus nominaalväärtusest,
     *      status - 1: ok (diff < alumine viga), 2: warning (diff < ülemine viga), 3: error (diff > ülemine viga),
     *      y - punkti tegelik väärtus
     */
    "analyzed_angles": ?Array<[IntegerType, IntegerType, number, null]>,
    /**
     *  Nominaalpunktide info (üks iga nominaalpunkti kohta):
     *  - Nominaali info: massiiv kujul
     *    [lineNo, pointName, isAdas], kus:
     *      lineNo = 0, ..., 5
     *      pointName = "Ai", "Bi", "Ci", "P50", "P100", ...
     *      isAdas - true/false/null
     */
    "lineinfo": Array<[IntegerType, string, ?boolean]>
};

export type ProductVersionsType = Array<string>;

export type WagonConfType = {
    /**
     * Kas tuleb teha servaotsing
     */
    "e": boolean,
    /**
     * Kas tuleb jõuga servaotsing peale suruda
     */
    "fe": boolean,
    /**
     * Kas mõõtmine on keelatud
     */
    "m": boolean,
    /**
     * Vaguni number
     */
    "n": IntegerType,
    /**
     * Vaguniga hetkel seotud toote kood (koos variatsiooniga, kui on määratud)
     */
    "pc": string,
    /**
     * Kas korduvmõõtmine on keelatud (enam ei ole kasutusel)
     */
    "r": boolean,
    /**
     * Painutusraami kood
     */
    "s": string
};


export type SessionType = {
    token: string,
    expires: string,
    user: string,
    role: string
};


export type LogonResultType = {
    logonResult: SessionType
};


export type UserInfoType = {
    username: string,
    password: string
}


/**
 * REST teenuste /wagons/{n} päringu vastus
 */
export type WagonsResultType = {
    "wagonsResult": ?MeasurementType
};

/**
 * REST teenuse /productVersions/{kood} päringu vastus
 */
export type ProductVersionsResultType = {
    "productVersionsResult": ProductVersionsType
};

/**
 * REST teenuse /wagonConf päringu vastus
 */
export type WagonConfResultType = {
    "wagonConfResult": Array<WagonConfType>
};

export type MeasurementsResultType = {
    "measurementsResult": Array<MeasurementType>
};

export type NominalGraphsResultType = {
    "nominalGraphsResult": MeasurementType
};


export type ErrorType = {
    code: string,
    internalMessage: ?string,
    message: string,
    trace: ?string,
    inner: ?{
        message: string,
        trace: string
    }
};

export type ErrorResultType = {
    error: ErrorType
};
